import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { trapTabKey } from '../hooks/useFocusTrap';
import { decodeImageFile } from '../lib/barcode';
import { useToast } from './Toast';

const TEXT_PROMPT = 'Toma una foto del código de barras para escanearlo.';
const TEXT_SCAN_CTA = 'Escanear';
const TEXT_PROCESSING = 'Procesando… espera un momento';
const TEXT_CHECKSUM_INVALID =
  'Ups, no pudimos validar el código. Acerca la cámara o intenta desde otro ángulo.';
const TEXT_NOT_FOUND = 'No detectamos ningún código. Intenta tomar otra foto.';
const TEXT_DECODER_FAILED =
  'Hubo un problema iniciando el lector de códigos. Intenta cerrar y reabrir el escáner.';

interface StillCaptureProps {
  onClose: () => void;
  onDecoded: (ean13: string) => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

/**
 * Tap-to-capture single-still fallback — the iOS Safari / WebKit path (issue
 * #167), chosen when {@link BarcodeScanner} finds no healthy native
 * `BarcodeDetector`. The operator taps "Escanear" → the OS opens the native
 * camera UI (`<input type="file" capture="environment">`) → one full-resolution
 * photo is decoded **once** via the `zxing-wasm` polyfill (`decodeImageFile`).
 *
 * By construction there is **no** `getUserMedia`, no `<video>`, and no tick loop
 * here — exactly the live-decode loop that crossed WebKit's Jetsam memory
 * ceiling and reloaded the tab. Zoom/torch/focus come from the OS camera UI,
 * so this path has no in-app camera controls or ROI reticle. The decoded-EAN
 * contract to the parent (`onDecoded`) is identical to the live path.
 */
export function StillCapture({ onClose, onDecoded, triggerRef }: StillCaptureProps): React.ReactElement {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onDecodedRef = useRef(onDecoded);
  onDecodedRef.current = onDecoded;
  const triggerRefStable = useRef(triggerRef);
  triggerRefStable.current = triggerRef;

  const { show: showToast } = useToast();
  // `busy` spans BOTH the local WASM decode AND the parent's product lookup
  // (onDecoded → api.suggest), so the idle "Escanear" prompt never flashes back
  // while a lookup is still in flight (#171). One spinner from "use this photo"
  // until the redirect/close.
  const [busy, setBusy] = useState(false);
  // Re-capturable outcomes: a matched-but-bad-checksum code, or nothing found.
  const [recoverable, setRecoverable] = useState<'checksum' | 'not-found' | null>(null);

  const close = (): void => {
    const trig = triggerRefStable.current?.current;
    if (trig && typeof trig.focus === 'function') trig.focus();
    onCloseRef.current();
  };

  // Track mount so async decode callbacks don't setState after unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initial focus.
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Esc to close.
  // biome-ignore lint/correctness/useExhaustiveDependencies: close uses refs only and is stable
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const openCamera = (): void => {
    inputRef.current?.click();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file (re-capture) fires onChange again.
    e.target.value = '';
    if (!file) return;
    setRecoverable(null);
    setBusy(true);
    try {
      const result = await decodeImageFile(file);
      if (!mountedRef.current) return;
      if ('ean13' in result) {
        if (typeof navigator.vibrate === 'function') navigator.vibrate(50);
        // Keep the spinner up through the parent's lookup. On the happy path the
        // parent navigates/closes and we unmount mid-spinner (no idle flash,
        // #171). Same "dead preview" contract as the live path: if onDecoded
        // rejects on a 5xx, the parent toasts and leaves the modal open — we
        // fall through and reset to idle so the operator can re-capture.
        try {
          await Promise.resolve(onDecodedRef.current(result.ean13));
        } catch (err) {
          if (mountedRef.current) console.warn('[scanner] onDecoded failed:', err);
        }
        if (mountedRef.current) setBusy(false);
        return;
      }
      setBusy(false);
      setRecoverable(result.reason);
    } catch (err) {
      if (!mountedRef.current) return;
      setBusy(false);
      console.warn('barcode scanner error', err);
      showToast(TEXT_DECODER_FAILED, 'error');
    }
  };

  const onKeyDownTrap = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const res = trapTabKey(e, dialogRef.current);
    if (res) {
      e.preventDefault();
      res.focusTarget.focus();
    }
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) close();
  };

  const message =
    recoverable === 'checksum' ? TEXT_CHECKSUM_INVALID : recoverable === 'not-found' ? TEXT_NOT_FOUND : null;

  return (
    <div data-testid="scan-backdrop" onClick={onBackdropClick} className="scan-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Escanear código de barras"
        onKeyDown={onKeyDownTrap}
        className="scan-modal scan-modal-still"
      >
        <button
          type="button"
          ref={closeBtnRef}
          aria-label="Cerrar"
          onClick={close}
          className="scan-pill scan-close"
        >
          ✕
        </button>

        {/* Native OS camera capture — hidden; the CTA below triggers it. The
            OS provides zoom/torch/focus, so there are no in-app controls. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={onFile}
        />

        <div className="scan-still">
          {busy ? (
            <p role="status" className="scan-still-msg scan-still-busy">
              <Loader2 className="animate-spin" size={22} aria-hidden="true" />
              {TEXT_PROCESSING}
            </p>
          ) : (
            <>
              <p className={`scan-still-msg ${message ? 'scan-still-msg-error' : ''}`} role="status">
                {message ?? TEXT_PROMPT}
              </p>
              <button type="button" className="btn btn-primary" onClick={openCamera}>
                {TEXT_SCAN_CTA}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
